import { configureBrowserSdk, createAppQueryClient } from '@repo/sdk'
import type { BreadcrumbFromQueryClient } from '@repo/ui/components/query-breadcrumbs'
import { getInitialTheme, type Theme } from '@repo/ui/theme'
import { hashKey, type QueryClient } from '@tanstack/react-query'
import { createRouter, rootRouteId } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import type { AppLanguageCode, TKey } from '@/lib/i18n'
import { getInitialAppLanguage, onAppLanguageChange } from '@/lib/i18n'
import { initSentry } from '@/lib/sentry'
import { makeQueryKeys } from '@/query/query-keys'
import { instanceBrandingQueryOptions } from '@/query/query-options'
import { ErrorPage, LoadingPage, NotFoundPage } from '@/routes/__root'
import { routeTree } from './routeTree.gen'

export interface RouterContext {
	appLanguage: AppLanguageCode
	theme: Theme
	queryClient: QueryClient
}

export function getRouter() {
	const queryClient = createAppQueryClient()
	const router = createRouter({
		routeTree,
		context: {
			appLanguage: getInitialAppLanguage(),
			theme: getInitialTheme(),
			queryClient
		} satisfies RouterContext,
		defaultPendingMs: 0,
		defaultPreload: 'intent',
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
		defaultErrorComponent: ErrorPage,
		defaultNotFoundComponent: NotFoundPage,
		defaultPendingComponent: LoadingPage
	})

	// Client bootstrap. `StartClient` and `hydrateStart` do not expose a
	// post-hydrate hook for one-time side effects, and pre-emptively calling
	// `hydrateStart()` from `client.tsx` creates a second router instance in
	// production (no HMR promise cache there) — bootstrap would attach to a
	// router that is never mounted via `RouterProvider`. Running here gates
	// off `router.isServer` so SSR stays untouched.
	if (!router.isServer) {
		initSentry(router)
		// Tab titles are built from route context, which `createRouter` above
		// fills once from `getInitialAppLanguage()` and nothing writes to again
		// (see `__root.tsx`'s `beforeLoad`). Invalidating every match on a language
		// change is what re-runs the `head()` functions, so the title follows
		// the picker instead of keeping the language the tab opened in
		// (OPSSTD-8813). It is not filtered to the root: `admin/route.tsx` and
		// `links/index.tsx` carry their own titles. Loaders re-run with it and
		// read through the query cache, so this fetches nothing.
		onAppLanguageChange(() => {
			router.invalidate()
		})
		// The document title is computed by the root route's `head`, which
		// runs when the root loads and not again — and a browser-only route
		// warms the branding after that. Re-running the root when the read
		// lands is what puts the reseller's name in the tab, and it cannot
		// loop: the second run finds the cache fresh and fetches nothing.
		queryClient.getQueryCache().subscribe((event) => {
			if (
				event.type === 'updated' &&
				event.action.type === 'success' &&
				event.query.queryHash ===
					hashKey(instanceBrandingQueryOptions().queryKey)
			) {
				router.invalidate({
					filter: (match) => match.routeId === rootRouteId
				})
			}
		})
		// The back office has a cached session again (T2.1.2), so both hooks
		// are wired here as they are on the client app. They read `csrfToken`
		// out of the session cache entry, which is why MS4 T4.6 could fill it
		// by replacing the resolver alone — this file did not change.
		configureBrowserSdk({
			queryClient,
			onUnauthorized: () => router.invalidate(),
			// The session is alive and only this page's copy of the CSRF
			// token is out of date, which is what happens to a page left
			// open across a password reset or a sign-out and back in.
			// Re-reading it is the whole recovery; `onUnauthorized` above
			// is the other case and clears everything.
			refreshSession: (client) =>
				client.refetchQueries({
					queryKey: makeQueryKeys.auth.session()
				}),
			getCsrfToken: (client) =>
				client.getQueryData<{ csrfToken?: string }>(
					makeQueryKeys.auth.session()
				)?.csrfToken,
			isSessionAuthenticated: (client) =>
				client.getQueryData<{ sessionType?: string }>(
					makeQueryKeys.auth.session()
				)?.sessionType === 'authenticated'
		})
	}

	setupRouterSsrQueryIntegration({
		router,
		queryClient
	})

	return router
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>
	}

	// Breadcrumb metadata for the shared @repo/ui query-breadcrumbs. Declared
	// per-app so the key is validated against this app's full TKey (the package
	// only knows the common namespace).
	interface StaticDataRouteOption {
		breadcrumb?:
			| TKey
			| { key: TKey; count?: number }
			| ((ctx: {
					params: Record<string, string>
					queryClient: QueryClient
			  }) => BreadcrumbFromQueryClient)
	}
}
